import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { useRealtime } from '@/realtime/useRealtime';
import { usePresenceQuery } from '@/services/api/presence/hooks/use-presence';

/**
 * Says whether live updates are actually arriving, and who else is here.
 *
 * Without it a disconnected app looks exactly like a connected one — the numbers simply
 * stop moving, which is the failure mode people trust least once they notice it. The count
 * rides along because it answers the same question from the other side: the platform is
 * live, and these many colleagues are on it. It is also the way in to the list, which no
 * longer has a row of its own in the nav.
 *
 * Disconnected, the count is whatever it was when the socket dropped, so it is withheld
 * rather than shown going stale — and the control stops being a link, because a number
 * nobody is updating should not invite a click.
 */
export function RealtimeIndicator() {
  const { t } = useTranslation();
  const { isConnected } = useRealtime();
  const { data } = usePresenceQuery();

  const dot = (
    <span
      aria-hidden
      className={cn(
        'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
        isConnected ? 'bg-emerald-500' : 'animate-pulse bg-amber-500'
      )}
    />
  );

  if (!isConnected) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground" title={t('realtime.offline_hint')}>
        {dot}
        <span className="hidden sm:inline">{t('realtime.reconnecting')}</span>
      </span>
    );
  }

  return (
    <Link
      to="/presence"
      title={t('realtime.live_hint')}
      className="flex items-center gap-1.5 rounded-sm text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {dot}
      <span className="hidden sm:inline">{t('presence.online_count', { count: data?.length ?? 0 })}</span>
    </Link>
  );
}
